import React, { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Chip,
  Stack,
  Button,
  Paper,
  Divider,
  InputBase,
  MenuItem,
  Select,
  FormControl,
  Breadcrumbs,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import LikedItemsContext from "../../../context/likedItems/LikedItemsContext";
import CartItemsContext from "../../../context/cartItems/CartItemsContext";
import RecentlyViewedContext from "../../../context/recentlyViewed/RecentlyViewedContext";
import ProductsContainer from "../../../components/app/Products/ProductsContainer";
import CustomButton from "../../../components/common/Button/CustomButton";
import CustomisedSnackbar from "../../../components/common/Snackbar/CustomisedSnackbar";
import AlertDialogSlide from "../../../components/common/Dialog/AlertDialogSlide";
import Loader from "../../../components/loader/Loader";
import ProductsAPI from "../../../api/firebase/ProductsAPI";

import metaPixel from "../../../utils/analytics/metaPixel";

const LikedItemsPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const likedItemsContext = useContext(LikedItemsContext);
  const cartItemsContext = useContext(CartItemsContext);
  const recentlyViewedContext = useContext(RecentlyViewedContext);

  const recentlyViewedItemsArray = recentlyViewedContext.state;

  const [likedItemsArray, setLikedItemsArray] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter & Sort state
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // 'all' | 'in-stock' | 'out-of-stock'
  const [sortBy, setSortBy] = useState("default"); // 'default' | 'price-asc' | 'price-desc' | 'discount' | 'rating'

  // Dialog & Toast state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: "",
    severity: "success",
    title: "",
  });

  // Initial load: Fetch fresh product details to ensure latest prices & stock
  useEffect(() => {
    let isMounted = true;
    const productsAPI = new ProductsAPI();
    const currentLiked = likedItemsContext.state || [];

    if (currentLiked.length > 0) {
      productsAPI
        .getProducts()
        .then((res) => {
          if (!isMounted) return;
          const allProducts = res?.data || [];
          if (allProducts.length > 0) {
            const latestLikedItems = currentLiked.map((item) => {
              const matched = allProducts.find((p) => p.id === item.id);
              return matched ? { ...matched } : item;
            });
            setLikedItemsArray(latestLikedItems);
          } else {
            setLikedItemsArray(currentLiked);
          }
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching latest products for wishlist:", err);
          if (isMounted) {
            setLikedItemsArray(currentLiked);
            setLoading(false);
          }
        });
    } else {
      setLikedItemsArray([]);
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run on mount

  // Sync when likedItemsContext.state changes (items added or removed via ProductCard)
  useEffect(() => {
    const currentLiked = likedItemsContext.state || [];
    setLikedItemsArray((prev) => {
      return currentLiked.map((item) => {
        const existing = prev.find((p) => p.id === item.id);
        return existing ? { ...existing, ...item } : item;
      });
    });
  }, [likedItemsContext.state]);

  // Calculate stats: total items, in-stock count, total selling price, MRP, and savings
  const stats = useMemo(() => {
    const items = likedItemsArray || [];
    let totalMRP = 0;
    let totalSellingPrice = 0;
    let inStockCount = 0;
    let outOfStockCount = 0;

    items.forEach((item) => {
      const listPrice = Number(item.listPrice) || 0;
      const maxPrice = Number(item.maxPrice) || listPrice;
      const stock = Number(item.unitsInStock);
      const isOut = Number.isFinite(stock) && stock <= 0;

      if (isOut) {
        outOfStockCount++;
      } else {
        inStockCount++;
      }
      totalSellingPrice += listPrice;
      totalMRP += maxPrice;
    });

    const totalSavings = Math.max(0, totalMRP - totalSellingPrice);
    const discountPercent =
      totalMRP > 0 ? Math.round((totalSavings / totalMRP) * 100) : 0;

    return {
      totalItems: items.length,
      inStockCount,
      outOfStockCount,
      totalSellingPrice,
      totalMRP,
      totalSavings,
      discountPercent,
    };
  }, [likedItemsArray]);

  // Filtered & sorted products based on search, status, and sort option
  const filteredAndSortedItems = useMemo(() => {
    let list = [...(likedItemsArray || [])];

    // 1. Search Query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      list = list.filter((item) => {
        const titleMatch = item.title?.toLowerCase().includes(query);
        const categoryMatch =
          item.parentCategory?.name?.toLowerCase().includes(query) ||
          item.parentCategory?.title?.toLowerCase().includes(query);
        return titleMatch || categoryMatch;
      });
    }

    // 2. Filter Status
    if (filterStatus === "in-stock") {
      list = list.filter((item) => {
        const stock = Number(item.unitsInStock);
        return !Number.isFinite(stock) || stock > 0;
      });
    } else if (filterStatus === "out-of-stock") {
      list = list.filter((item) => {
        const stock = Number(item.unitsInStock);
        return Number.isFinite(stock) && stock <= 0;
      });
    }

    // 3. Sort Order
    if (sortBy === "price-asc") {
      list.sort(
        (a, b) => (Number(a.listPrice) || 0) - (Number(b.listPrice) || 0)
      );
    } else if (sortBy === "price-desc") {
      list.sort(
        (a, b) => (Number(b.listPrice) || 0) - (Number(a.listPrice) || 0)
      );
    } else if (sortBy === "discount") {
      list.sort((a, b) => {
        const discA =
          ((Number(a.maxPrice) || 0) - (Number(a.listPrice) || 0)) /
          (Number(a.maxPrice) || 1);
        const discB =
          ((Number(b.maxPrice) || 0) - (Number(b.listPrice) || 0)) /
          (Number(b.maxPrice) || 1);
        return discB - discA;
      });
    } else if (sortBy === "rating") {
      list.sort((a, b) => {
        const avgA =
          a.ratings && a.ratings.length
            ? a.ratings.reduce((x, y) => x + y, 0) / a.ratings.length
            : 0;
        const avgB =
          b.ratings && b.ratings.length
            ? b.ratings.reduce((x, y) => x + y, 0) / b.ratings.length
            : 0;
        return avgB - avgA;
      });
    }

    return list;
  }, [likedItemsArray, searchQuery, filterStatus, sortBy]);

  // Add all available in-stock items to cart
  const handleAddAllToCart = () => {
    if (!likedItemsArray || likedItemsArray.length === 0) return;

    const currentCart = cartItemsContext.state || [];
    let addedCount = 0;
    const newCart = [...currentCart];

    likedItemsArray.forEach((product) => {
      const stock = Number(product.unitsInStock);
      const isOutOfStock = Number.isFinite(stock) && stock <= 0;
      if (isOutOfStock) return;

      const existingIndex = newCart.findIndex((item) => item.id === product.id);
      if (existingIndex !== -1) {
        const currentQty = newCart[existingIndex].quantity || 1;
        const limitReached = Number.isFinite(stock) && currentQty >= stock;
        if (!limitReached) {
          newCart[existingIndex] = {
            ...newCart[existingIndex],
            quantity: currentQty + 1,
          };
          addedCount++;
        }
      } else {
        newCart.push({
          ...product,
          quantity: 1,
        });
        addedCount++;
      }
    });

    if (addedCount > 0) {
      cartItemsContext.setState(newCart);
      newCart.forEach((item) => {
        const previousQuantity = Number(currentCart.find((previous) => previous.id === item.id)?.quantity || 0);
        const addedQuantity = Number(item.quantity) - previousQuantity;
        if (addedQuantity > 0) metaPixel.addToCart(item, addedQuantity);
      });
      setSnackbarState({
        open: true,
        message: `${addedCount} ${
          addedCount === 1 ? "item" : "items"
        } added to your cart`,
        severity: "success",
        title: "Cart Updated",
      });
    } else {
      setSnackbarState({
        open: true,
        message: "All in-stock wishlist items are already in your cart",
        severity: "info",
        title: "Already in Cart",
      });
    }
  };

  // Clear entire wishlist
  const handleConfirmClearWishlist = () => {
    likedItemsContext.setState([]);
    setLikedItemsArray([]);
    setClearDialogOpen(false);
    setSnackbarState({
      open: true,
      message: "All items have been removed from your wishlist",
      severity: "remove",
      title: "Wishlist Cleared",
    });
  };

  // Reset filters
  const handleResetFilters = () => {
    setSearchQuery("");
    setFilterStatus("all");
    setSortBy("default");
  };

  const handleCloseSnackbar = () => {
    setSnackbarState((prev) => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Box sx={{ py: 8 }}>
        <Loader type="LINEAR" fullHeight={false} />
      </Box>
    );
  }

  const hasItems = likedItemsArray && likedItemsArray.length > 0;

  return (
    <Box
      sx={{
        minHeight: "85vh",
        bgcolor: "#f8faf9",
        pt: { xs: 2, sm: 3 },
        pb: { xs: 5, sm: 8 },
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 1.5, sm: 3, md: 4 } }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}
          aria-label="breadcrumb"
          sx={{ mb: { xs: 2, sm: 2.5 } }}
        >
          <Typography
            component={Link}
            to="/"
            variant="body2"
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": { color: "#075e54", textDecoration: "underline" },
            }}
          >
            Home
          </Typography>
          <Typography
            component={Link}
            to="/profile"
            variant="body2"
            sx={{
              color: "#64748b",
              textDecoration: "none",
              fontWeight: 500,
              "&:hover": { color: "#075e54", textDecoration: "underline" },
            }}
          >
            My Account
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#075e54", fontWeight: 700 }}
          >
            My Wishlist
          </Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            gap: 2,
            mb: { xs: 2.5, md: 3 },
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  color: "#143833",
                  fontSize: { xs: "1.65rem", sm: "2rem", md: "2.25rem" },
                  letterSpacing: "-0.02em",
                }}
              >
                My Wishlist
              </Typography>
              {hasItems && (
                <Chip
                  label={`${stats.totalItems} ${
                    stats.totalItems === 1 ? "Item" : "Items"
                  }`}
                  size="small"
                  sx={{
                    bgcolor: "#e6f4f1",
                    color: "#075e54",
                    fontWeight: 700,
                    fontSize: "0.82rem",
                    height: 28,
                    borderRadius: "999px",
                    px: 0.5,
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="body2"
              sx={{
                color: "#5b6b68",
                mt: 0.5,
                maxWidth: 620,
                fontSize: { xs: "0.84rem", sm: "0.92rem" },
              }}
            >
              {hasItems
                ? "Your saved Ayurvedic remedies and personal care favorites. Easily move them to cart or keep them for future orders."
                : "Save your favorite Ayurvedic health & wellness products here to order anytime."}
            </Typography>
          </Box>

          {/* Quick Header Actions (when items exist) */}
          {hasItems && (
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              <Button
                variant="outlined"
                color="inherit"
                size={isMobile ? "small" : "medium"}
                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setClearDialogOpen(true)}
                sx={{
                  borderRadius: "10px",
                  borderColor: "#d5e0dc",
                  color: "#5b6b68",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.8rem", sm: "0.88rem" },
                  px: { xs: 1.5, sm: 2 },
                  py: 0.8,
                  "&:hover": {
                    borderColor: "#ef4444",
                    color: "#ef4444",
                    bgcolor: "rgba(239, 68, 68, 0.04)",
                  },
                }}
              >
                Clear All
              </Button>
              <CustomButton
                variant="contained"
                size={isMobile ? "small" : "medium"}
                startIcon={<ShoppingCartRoundedIcon sx={{ fontSize: 19 }} />}
                onClick={handleAddAllToCart}
                disabled={stats.inStockCount === 0}
                sx={{
                  flexGrow: { xs: 1, sm: 0 },
                  borderRadius: "10px",
                  background:
                    "linear-gradient(135deg, #075e54 0%, #004d40 100%)",
                  boxShadow: "0 4px 14px rgba(7, 94, 84, 0.25)",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: { xs: "0.82rem", sm: "0.9rem" },
                  px: { xs: 2, sm: 2.5 },
                  py: 0.9,
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #0a6f63 0%, #075e54 100%)",
                    boxShadow: "0 6px 18px rgba(7, 94, 84, 0.35)",
                  },
                }}
              >
                Add All to Cart
              </CustomButton>
            </Stack>
          )}
        </Box>



        {/* Filter & Sort Toolbar (when items exist) */}
        {hasItems && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              mb: 3,
              borderRadius: "14px",
              border: "1px solid #e2ece8",
              bgcolor: "#ffffff",
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            {/* Status Filter Chips */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ overflowX: "auto", pb: { xs: 0.5, sm: 0 } }}
            >
              <Chip
                label={`All (${stats.totalItems})`}
                size="small"
                clickable
                onClick={() => setFilterStatus("all")}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: filterStatus === "all" ? "#075e54" : "#f1f5f4",
                  color: filterStatus === "all" ? "#ffffff" : "#4a5d59",
                  "&:hover": {
                    bgcolor: filterStatus === "all" ? "#075e54" : "#e6eeec",
                  },
                }}
              />
              <Chip
                label={`In Stock (${stats.inStockCount})`}
                size="small"
                clickable
                onClick={() => setFilterStatus("in-stock")}
                sx={{
                  fontWeight: 600,
                  borderRadius: "8px",
                  bgcolor: filterStatus === "in-stock" ? "#075e54" : "#f1f5f4",
                  color: filterStatus === "in-stock" ? "#ffffff" : "#4a5d59",
                  "&:hover": {
                    bgcolor:
                      filterStatus === "in-stock" ? "#075e54" : "#e6eeec",
                  },
                }}
              />
              {stats.outOfStockCount > 0 && (
                <Chip
                  label={`Out of Stock (${stats.outOfStockCount})`}
                  size="small"
                  clickable
                  onClick={() => setFilterStatus("out-of-stock")}
                  sx={{
                    fontWeight: 600,
                    borderRadius: "8px",
                    bgcolor:
                      filterStatus === "out-of-stock" ? "#075e54" : "#f1f5f4",
                    color:
                      filterStatus === "out-of-stock" ? "#ffffff" : "#4a5d59",
                    "&:hover": {
                      bgcolor:
                        filterStatus === "out-of-stock" ? "#075e54" : "#e6eeec",
                    },
                  }}
                />
              )}
            </Stack>

            {/* Search Input & Sort Dropdown */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {/* Search Bar */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "10px",
                  border: "1px solid #d5e0dc",
                  bgcolor: "#fbfcfb",
                  width: { xs: "100%", sm: 220, md: 260 },
                  "&:focus-within": {
                    borderColor: "#075e54",
                    bgcolor: "#ffffff",
                  },
                }}
              >
                <SearchRoundedIcon sx={{ fontSize: 18, color: "#8a9b97", mr: 1 }} />
                <InputBase
                  placeholder="Filter wishlist..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    fontSize: "0.85rem",
                    width: "100%",
                    color: "#143833",
                  }}
                />
                {searchQuery && (
                  <CloseRoundedIcon
                    onClick={() => setSearchQuery("")}
                    sx={{
                      fontSize: 16,
                      color: "#8a9b97",
                      cursor: "pointer",
                      "&:hover": { color: "#143833" },
                    }}
                  />
                )}
              </Box>

              {/* Sort Selector */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  displayEmpty
                  IconComponent={FilterListRoundedIcon}
                  sx={{
                    fontSize: "0.85rem",
                    borderRadius: "10px",
                    fontWeight: 600,
                    color: "#143833",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#d5e0dc",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#075e54",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#075e54",
                    },
                  }}
                >
                  <MenuItem value="default" sx={{ fontSize: "0.85rem" }}>
                    Sort: Recently Added
                  </MenuItem>
                  <MenuItem value="price-asc" sx={{ fontSize: "0.85rem" }}>
                    Price: Low to High
                  </MenuItem>
                  <MenuItem value="price-desc" sx={{ fontSize: "0.85rem" }}>
                    Price: High to Low
                  </MenuItem>
                  <MenuItem value="discount" sx={{ fontSize: "0.85rem" }}>
                    Biggest Discount
                  </MenuItem>
                  <MenuItem value="rating" sx={{ fontSize: "0.85rem" }}>
                    Top Rated
                  </MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Paper>
        )}

        {/* Wishlist Products Grid (when items match filters) */}
        {hasItems && filteredAndSortedItems.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <ProductsContainer
              heading=""
              showInRow={false}
              addToCartEnable={true}
              showCarousel={false}
              productsArray={filteredAndSortedItems}
              fadeOutOnRemovingLike={true}
              gridColumns={4}
            />
          </Box>
        )}

        {/* Filter Empty State (when items exist in wishlist, but search/filter yields 0) */}
        {hasItems && filteredAndSortedItems.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              my: 4,
              textAlign: "center",
              borderRadius: "18px",
              border: "1px dashed #cde0dc",
              bgcolor: "#ffffff",
            }}
          >
            <FilterListRoundedIcon sx={{ fontSize: 44, color: "#8a9b97", mb: 1.5 }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#143833", mb: 0.5 }}
            >
              No matching products found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#64748b", maxWidth: 420, mx: "auto", mb: 2.5 }}
            >
              No items in your wishlist match "{searchQuery || filterStatus}". Try
              adjusting your search query or status filter.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<RestartAltRoundedIcon />}
              onClick={handleResetFilters}
              sx={{
                borderRadius: "10px",
                borderColor: "#075e54",
                color: "#075e54",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  borderColor: "#054942",
                  bgcolor: "rgba(7, 94, 84, 0.05)",
                },
              }}
            >
              Reset Filters
            </Button>
          </Paper>
        )}

        {/* Empty Wishlist State (when 0 items total) */}
        {!hasItems && (
          <Box
            sx={{
              py: { xs: 3, md: 5 },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 680,
                textAlign: "center",
                p: { xs: 3, sm: 5 },
                borderRadius: "24px",
                border: "1px solid #e0ece8",
                background:
                  "linear-gradient(180deg, #ffffff 0%, #fbfdfc 100%)",
                boxShadow: "0 8px 30px rgba(7, 94, 84, 0.05)",
              }}
            >
              {/* Pulse Heart Badge */}
              <Box
                sx={{
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, rgba(7, 94, 84, 0.1) 0%, rgba(18, 140, 126, 0.16) 100%)",
                  color: "#075e54",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                  boxShadow: "0 8px 20px rgba(7, 94, 84, 0.12)",
                }}
              >
                <FavoriteBorderRoundedIcon
                  sx={{ fontSize: 48, color: "#075e54" }}
                />
              </Box>

              <Typography
                variant="h5"
                component="h2"
                sx={{
                  fontWeight: 800,
                  mb: 1,
                  color: "#143833",
                  letterSpacing: "-0.01em",
                }}
              >
                Your Wishlist is Empty
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  color: "#5b6b68",
                  maxWidth: 480,
                  mx: "auto",
                  mb: 3.5,
                  lineHeight: 1.6,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                }}
              >
                Explore our collection of handcrafted Ayurvedic remedies, herbal
                oils, natural teas, and daily wellness essentials to save your
                favorites here.
              </Typography>

              {/* Action Buttons */}
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 4 }}
              >
                <CustomButton
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardRoundedIcon />}
                  onClick={() => navigate("/collections")}
                  sx={{
                    px: 4,
                    py: 1.3,
                    borderRadius: "12px",
                    fontWeight: 700,
                    textTransform: "none",
                    background:
                      "linear-gradient(135deg, #075e54 0%, #004d40 100%)",
                    boxShadow: "0 6px 18px rgba(7, 94, 84, 0.25)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #0a6f63 0%, #075e54 100%)",
                      boxShadow: "0 8px 24px rgba(7, 94, 84, 0.35)",
                    },
                  }}
                >
                  Explore Products
                </CustomButton>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<ShoppingBagOutlinedIcon />}
                  onClick={() => navigate("/cart")}
                  sx={{
                    px: 3,
                    py: 1.3,
                    borderRadius: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    color: "#143833",
                    borderColor: "#cde0dc",
                    "&:hover": {
                      borderColor: "#075e54",
                      bgcolor: "rgba(7, 94, 84, 0.04)",
                    },
                  }}
                >
                  View Cart
                </Button>
              </Stack>

              {/* Trust Badges */}
              <Grid
                container
                spacing={2}
                sx={{
                  mt: 3,
                  pt: 3,
                  borderTop: "1px solid #e8f0ec",
                }}
              >
                <Grid item xs={12} sm={4}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <SpaOutlinedIcon sx={{ fontSize: 20, color: "#075e54" }} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      100% Ayurvedic
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <LocalShippingOutlinedIcon
                      sx={{ fontSize: 20, color: "#075e54" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      Free Shipping on ₹499+
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                  >
                    <VerifiedOutlinedIcon
                      sx={{ fontSize: 20, color: "#075e54" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      Guaranteed Authentic
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Recently Viewed Items Section */}
        {recentlyViewedItemsArray !== null &&
          recentlyViewedItemsArray.length > 0 && (
            <Box sx={{ mt: { xs: 5, md: 7 } }}>
              <Divider sx={{ mb: 3, borderColor: "#e2ece8" }} />
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 2 }}
              >
                <HistoryRoundedIcon sx={{ color: "#075e54", fontSize: 24 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#143833",
                    fontSize: { xs: "1.1rem", sm: "1.25rem" },
                  }}
                >
                  Recently Viewed
                </Typography>
              </Stack>
              <ProductsContainer
                heading=""
                showInRow={true}
                showScrollButtons={true}
                addToCartEnable={true}
                showCarousel={false}
                productsArray={recentlyViewedItemsArray}
                showRatingNumber={false}
              />
            </Box>
          )}

        {/* Clear Wishlist Confirmation Dialog */}
        <AlertDialogSlide
          isOpen={clearDialogOpen}
          handleOnClose={() => setClearDialogOpen(false)}
          handleOnAgree={handleConfirmClearWishlist}
          title="Clear entire wishlist?"
          description={`Are you sure you want to remove all ${stats.totalItems} items from your wishlist? You will have to search and save them again.`}
          agreeButtonText="Remove All"
          cancelButtonText="Keep Items"
        />

        {/* Feedback Snackbar */}
        <CustomisedSnackbar
          open={snackbarState.open}
          message={snackbarState.message}
          severity={snackbarState.severity}
          title={snackbarState.title}
          handleClose={handleCloseSnackbar}
        />
      </Container>
    </Box>
  );
};

export default LikedItemsPage;
